import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Mail, ArrowRight, Loader } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [verificationData, setVerificationData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/auth/verify-email/${token}`);
      setVerificationStatus('success');
      setVerificationData(response.data);
    } catch (error) {
      // Check if user is already verified
      if (error.response?.data?.detail === 'already_verified') {
        setVerificationStatus('already_verified');
        setErrorMessage('Din e-postadresse er allerede bekreftet.');
      } else {
        setVerificationStatus('error');
        if (error.response?.data?.detail) {
          setErrorMessage(error.response.data.detail);
        } else {
          setErrorMessage('Kunne ikke verifisere e-postadressen. Lenken kan være ugyldig eller utløpt.');
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        
        {/* Loading State */}
        {verificationStatus === 'loading' && (
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-blue-500 rounded-full flex items-center justify-center animate-pulse">
              <Loader className="h-8 w-8 text-white animate-spin" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-white">
              Verifiserer e-postadresse...
            </h2>
            <p className="mt-2 text-gray-400">
              Vennligst vent mens vi bekrefter din e-postadresse.
            </p>
          </div>
        )}

        {/* Success State */}
        {verificationStatus === 'success' && (
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-white">
              E-postadresse bekreftet! 🎉
            </h2>
            
            {verificationData && (
              <div className="mt-6 bg-gray-800 rounded-lg p-6 text-left">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-400">Navn:</p>
                    <p className="text-white font-medium">{verificationData.full_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">E-post:</p>
                    <p className="text-white font-medium">{verificationData.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Organisasjon:</p>
                    <p className="text-white font-medium">{verificationData.organization_name}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 bg-blue-600/20 border border-blue-600/50 rounded-md p-4">
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-left">
                  <p className="text-blue-400 font-medium mb-1">Neste steg:</p>
                  <p className="text-blue-300 text-sm">
                    Kontoen din venter nå på godkjenning fra en skyteleder i {verificationData?.organization_name}. 
                    Du vil motta en e-post når kontoen din er godkjent.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <Link
                to="/login"
                className="group relative w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-gray-900 bg-yellow-500 hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-all"
              >
                Gå til innlogging
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        )}

        {/* Error State */}
        {verificationStatus === 'error' && (
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-red-500 rounded-full flex items-center justify-center">
              <XCircle className="h-10 w-10 text-white" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-white">
              Verifisering feilet
            </h2>
            
            <div className="mt-6 bg-red-600/20 border border-red-600/50 rounded-md p-4">
              <p className="text-red-400 text-sm">
                {errorMessage}
              </p>
            </div>

            <div className="mt-6 bg-gray-800 rounded-lg p-6 text-left">
              <p className="text-white font-medium mb-3">Mulige årsaker:</p>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Lenken har utløpt (gyldig i 24 timer)
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Lenken er allerede brukt
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-500 mr-2">•</span>
                  Ugyldig eller ødelagt lenke
                </li>
              </ul>
            </div>

            <div className="mt-8 space-y-3">
              <Link
                to="/register"
                className="group relative w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-gray-900 bg-yellow-500 hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-all"
              >
                Registrer deg på nytt
              </Link>
              
              <Link
                to="/login"
                className="block w-full text-center py-3 px-4 border border-gray-700 text-sm font-medium rounded-md text-gray-300 hover:bg-gray-800 transition-all"
              >
                Gå til innlogging
              </Link>
            </div>
          </div>
        )}

        {/* Already Verified State */}
        {verificationStatus === 'already_verified' && (
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-blue-500 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-white">
              Allerede bekreftet! ✅
            </h2>
            
            <div className="mt-6 bg-blue-600/20 border border-blue-600/50 rounded-md p-4">
              <p className="text-blue-300 text-sm">
                Din e-postadresse er allerede bekreftet. Du kan logge inn med brukernavnet og passordet ditt når kontoen din er godkjent av en skyteleder.
              </p>
            </div>

            <div className="mt-6 bg-gray-800 rounded-lg p-6 text-left">
              <p className="text-white font-medium mb-3">💡 Verifiseringslenker kan kun brukes én gang</p>
              <p className="text-sm text-gray-300">
                Av sikkerhetsgrunner kan hver verifiseringslenke kun brukes én gang. 
                Hvis du allerede har bekreftet e-posten din, trenger du ikke å gjøre det igjen.
              </p>
            </div>

            <div className="mt-8">
              <Link
                to="/login"
                className="group relative w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-gray-900 bg-yellow-500 hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-all"
              >
                Gå til innlogging
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default VerifyEmail;
